import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgClass, NgStyle } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { finalize, fromEvent, map, take, takeUntil, tap } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ControlValueAccessor } from 'eurogard-forms';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MtxDatetimepicker,
  MtxDatetimepickerInput,
  MtxDatetimepickerToggle,
} from '@ng-matero/extensions/datetimepicker';
import { MatDialog } from '@angular/material/dialog';
import {
  CopyTimeslotDialogComponent,
} from 'angular-controls/timeslot-control/copy-timeslot-dialog/copy-timeslot-dialog.component';
import { TimeslotDto } from 'open-api-service-app-settings/components/timeslot-dto';

export class Timeslot {

  static FromTime(
    from: string,
    to: string,
    active = false,
  ): Timeslot {
    const start = Timeslot.timeToPercent(from);
    const end   = Timeslot.timeToPercent(to);
    return new Timeslot(start, end, null, null, active);
  }

  private static timeToPercent(time: string): number {
    const [ hours, minutes ] = time.split(':');
    return (parseInt(hours) + parseInt(minutes) / 60) / 24;
  }

  private static percentToTime(percent: number): string {
    const hour = Math.floor(percent * 24).toFixed(0).padStart(2, '0');
    const min  = Math.floor(this.to5min(percent * 24 - Math.floor(percent * 24)) * 60).toFixed(0).padStart(2, '0');
    return `${ hour }:${ min }`;
  }

  private static percentToDateTime(percent: number): Date {
    const time    = this.percentToTime(percent);
    const hours   = parseInt(time.split(':')[0]);
    const minutes = parseInt(time.split(':')[1]);
    return new Date(0, 0, 0, hours, minutes);
  }

  private static dateTimeToPercent(date: Date): number {
    return (date.getHours() + date.getMinutes() / 60) / 24;
  }

  private static to5min(number: number): number {
    return Math.floor(number * 12) / 12;
  }

  /**
   * The start of the time slot in percent of the total time
   */
  public readonly start: WritableSignal<number>;
  /**
   * The end of the time slot in percent of the total time
   */
  public readonly end: WritableSignal<number>;
  public readonly width                                     = computed(() => this.end() - this.start());
  public readonly active: WritableSignal<boolean>;
  public readonly startTime                                 = computed<string>(() => {
    return Timeslot.percentToTime(this.start());
  });
  public readonly endTime                                   = computed<string>(() => {
    return Timeslot.percentToTime(this.end());
  });
  public readonly startDateTime                             = computed<Date>(() => {
    const [ hours, minutes ] = this.startTime().split(':');
    return new Date(0, 0, 0, parseInt(hours), parseInt(minutes));
  });
  public readonly endDateTime                               = computed<Date>(() => {
    const [ hours, minutes ] = this.endTime().split(':');
    return new Date(0, 0, 0, parseInt(hours), parseInt(minutes));
  });
  public readonly previous: WritableSignal<Timeslot | null> = signal(null);

  public readonly minNewStart         = computed<number>(() => {
    const previous = this.previous();
    let min        = 0;
    if (previous) {
      const dateTime = Timeslot.percentToDateTime(previous.start());
      dateTime.setMinutes(dateTime.getMinutes() + 30);
      min = Timeslot.dateTimeToPercent(dateTime);
    }
    return Math.max(min, 0);
  });
  public readonly maxNewStart         = computed<number>(() => {
    const endDateTime = Timeslot.percentToDateTime(this.end());
    endDateTime.setMinutes(endDateTime.getMinutes() - 30);
    return Timeslot.dateTimeToPercent(endDateTime);
  });
  public readonly maxNewStartDateTime = computed<Date>(() => Timeslot.percentToDateTime(this.maxNewStart()));
  public readonly minNewStartDateTime = computed<Date>(() => Timeslot.percentToDateTime(this.minNewStart()));

  public readonly next: WritableSignal<Timeslot | null> = signal(null);

  public readonly maxNewEnd         = computed<number>(() => {
    const next = this.next();
    const max  = 1;
    if (next) {
      const dateTime = Timeslot.percentToDateTime(next.end());
      dateTime.setMinutes(dateTime.getMinutes() - 30);
      return Timeslot.dateTimeToPercent(dateTime);
    }
    return Math.min(1, max);
  });
  public readonly minNewEnd         = computed<number>(() => {
    const startDateTime = Timeslot.percentToDateTime(this.start());
    startDateTime.setMinutes(startDateTime.getMinutes() + 30);
    return Timeslot.dateTimeToPercent(startDateTime);
  });
  public readonly maxNewEndDateTime = computed<Date>(() => Timeslot.percentToDateTime(this.maxNewEnd()));
  public readonly minNewEndDateTime = computed<Date>(() => Timeslot.percentToDateTime(this.minNewEnd()));

  constructor(
    start                     = 0,
    end                       = 1,
    previuos: Timeslot | null = null,
    next: Timeslot | null     = null,
    active                    = false,
  ) {
    this.start  = signal(start);
    this.end    = signal(end);
    this.active = signal(active);
    this.previous.set(previuos);
    this.next.set(next);
  }

  public setStartFromDateTime(date: Date) {
    if (!(date instanceof Date)) return;
    const start = Timeslot.dateTimeToPercent(date);
    if (start >= this.end()) return;
    this.start.set(start);
    this.previous()?.end.set(start);
  }

  public setEndFromDateTime(date: Date) {
    if (!(date instanceof Date)) return;
    const end = Timeslot.dateTimeToPercent(date);
    if (end <= this.start()) return;
    this.end.set(end);
    this.next()?.start.set(end);
  }

}

@Component({
  selector: 'eurogard-timeslot-control',
  standalone: true,
  imports: [
    MatButtonToggleModule,
    NgClass,
    MatIconModule,
    MatButton,
    FormsModule,
    MatSlideToggle,
    NgStyle,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MtxDatetimepicker,
    MtxDatetimepickerInput,
    MtxDatetimepickerToggle,
  ],
  templateUrl: './timeslot-control.component.html',
  styleUrl: './timeslot-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeslotControlComponent),
      multi: true,
    },
  ],
})
export class TimeslotControlComponent extends ControlValueAccessor {

  @Input() dayIndex!: number;
  @Input() readOnly = false;

  @Input() enforceCompactDesign = false;

  @Output() copyConfiguration = new EventEmitter<Array<boolean>>();

  public readonly slots = signal<Timeslot[]>([]);

  public readonly selectedSlot = signal<Timeslot | null>(null);

  private readonly dialog = inject(MatDialog);

  override writeValue(value: TimeslotDto[] | null) {
    if (!value?.length) {
      this.slots.set([]);
    }
    if (value?.length && value[value.length - 1].to === '23:59') {
      value[value.length - 1].to = '24:00';
    }
    const slots = value?.map(slot => Timeslot.FromTime(slot.from, slot.to, true)) ?? [];
    const filledSlots = this.fillSlotGaps(slots);
    const selected = Math.floor((filledSlots.length - 1) / 2);
    this.selectedSlot.set(filledSlots[selected]);
    this.slots.set(filledSlots);
  }

  onMousedown($event: MouseEvent, index: number, container: HTMLElement, isStart: boolean) {
    if (this.readOnly) return;

    const containerWidth = container.offsetWidth;

    let mouseX = 0;

    mouseX = $event.pageX;
    const slots = this.slots();
    const currentSlot = slots[index];
    let nextSlot: Timeslot | null = null;
    if (isStart) {
      if (index > 0) {
        nextSlot = slots[index - 1];
      }
    } else {
      if (index < slots.length - 1) {
        nextSlot = slots[index + 1];
      }
    }
    const minNewStart = currentSlot.minNewStart();
    const maxNewStart = currentSlot.maxNewStart();
    const maxNewEnd   = currentSlot.maxNewEnd();
    const minNewEnd   = currentSlot.minNewEnd();
    const currentValue         = isStart ? currentSlot.start : currentSlot.end;
    const originalCurrentValue = currentValue();

    const nextValue = nextSlot ? (isStart ? nextSlot.end : nextSlot.start) : null;

    fromEvent<MouseEvent>(window, 'mousemove').pipe(
      takeUntil(fromEvent(window, 'mouseup')),
      map((event) => event.pageX - mouseX),
      map(deltaX => deltaX / containerWidth),
      map(percent => originalCurrentValue + percent),
      map(percent => {
        if (isStart) {
          return Math.min(maxNewStart, Math.max(minNewStart, percent));
        }
        return Math.max(minNewEnd, Math.min(maxNewEnd, percent));
      }),
      tap(percent => {
        currentValue.set(percent);
        if (nextValue) {
          nextValue.set(percent);
        }
      }),
      finalize(() => {
        this.pushChanges();
      }),
    ).subscribe();
  }

  addSlot() {
    const selectedSlot = this.selectedSlot() ?? this.slots()[0];
    const start = selectedSlot.start() + selectedSlot.width() / 2;
    const end = selectedSlot.end();
    selectedSlot.end.set(start);
    const slot = new Timeslot(start, end);
    const selectedSlotIndex = this.slots().indexOf(selectedSlot);
    this.slots.update(slots => {
      const clone = [ ...slots ];
      clone.splice(selectedSlotIndex + 1, 0, slot);
      this.selectedSlot.set(slot);
      this.updatePreviousAndNext(clone);
      return clone;
    });
    this.pushChanges();
  }

  async removeSlot() {
    const selectedSlot = this.selectedSlot();
    if (!selectedSlot) {
      return;
    }
    const selectedSlotIndex = this.slots().indexOf(selectedSlot);
    const previousSlot = this.slots()[selectedSlotIndex - 1] ?? null;
    const nextSlot = this.slots()[selectedSlotIndex + 1] ?? null;
    if (previousSlot) {
      previousSlot.end.set(selectedSlot.end());
    } else if (nextSlot) {
      nextSlot.start.set(selectedSlot.start());
    } else {
      return;
    }
    this.slots.update(slots => {
      const clone = [ ...slots ];
      clone.splice(selectedSlotIndex, 1);
      this.selectedSlot.set(clone[selectedSlotIndex - 1] ?? clone[selectedSlotIndex]);
      this.updatePreviousAndNext(clone);
      return clone;
    });
    this.pushChanges();
  }

  setSlotActiveState(slot: Timeslot, $event: boolean) {
    slot.active.set($event);
    this.pushChanges();
  }

  private updatePreviousAndNext(slots: Timeslot[]) {
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      slot.previous.set(slots[i - 1] ?? null);
      slot.next.set(slots[i + 1] ?? null);
    }
  }

  private fillSlotGaps(slots: Timeslot[]): Timeslot[] {
    if (slots.length === 0) {
      return [ new Timeslot() ];
    }
    slots.sort((a, b) => a.start() - b.start());
    const filledSlots: Timeslot[]     = [];
    let previousSlot: Timeslot | null = null;
    for (const slot of slots) {
      if (previousSlot) {
        if (previousSlot.end() < slot.start()) {
          filledSlots.push(new Timeslot(previousSlot.end(), slot.start()));
        }
      } else if (slot.start() > 0) {
        filledSlots.push(new Timeslot(0, slot.start()));
      }
      filledSlots.push(slot);
      previousSlot = slot;
    }
    const lastSlot = filledSlots[filledSlots.length - 1];
    if (lastSlot.end() < 1) {
      filledSlots.push(new Timeslot(lastSlot.end(), 1));
    }
    this.updatePreviousAndNext(filledSlots);
    return filledSlots;
  }

  pushChanges() {
    const slots = this.slots();
    const activeSlots  = slots.filter(slot => slot.active());
    const timeslotList = activeSlots.map(slot => ({from: slot.startTime(), to: slot.endTime()}));
    if (timeslotList.length && timeslotList[timeslotList.length - 1]?.to === '24:00') {
      timeslotList[timeslotList.length - 1].to = '23:59';
    }
    this.onChange?.(timeslotList);
  }

  openCopyDialog() {
    this.dialog.open(CopyTimeslotDialogComponent, {
      data: { dayIndex: this.dayIndex }
    }).afterClosed().pipe(take(1)).subscribe(dayIndexes => {
      if (dayIndexes !== undefined) {
        this.copyConfiguration.emit(dayIndexes);
      }
    });
  }

}
